"use client"
import { Spin, Typography } from "antd"
import { useRouter } from "next/navigation"
import { useFavorite } from "@utils/hooks/useFavorite"
import React, { Suspense, useCallback, useLayoutEffect, useMemo } from "react"
import { updateListingLike } from "@components/Listings/listings-page-utils"
import { useHistory } from "@components/RouterHistory"
import { useMediaQuery } from "@utils/hooks/useMediaQuery"
import { findGroup, prepareData } from "@components/Listings/Chart/SoloResortChart/prepareData"
import styles from "@css/modules/Listing.module.scss"
import { Like } from "@css/icons/Like"
import cn from "classnames"
import Link from "next/link"
import { ArrowDown } from "@css/icons/SVG"
import { SlideAppear } from "@components/Animation/SlideAppear"
import { SoloResortChart } from "@components/Listings/Chart/SoloResortChart/SoloResortChart"
import { DealScore } from "@components/Listings/DealScore"
import { JsonLd } from "@components/JsonLd"
import { ListingPricing } from "@components/Listings/ListingPricing/ListingPricing"
import { PointsSummary } from "@components/Listings/PointsSummary"
import { HistoricalDues } from "@components/Resort/HistoricalDues"
import { useListingDealScore } from "@utils/hooks/useListingDealScore"
import { ListingStatus } from "@components/Listings/ListingStatus"
import { Listing } from "@t/Listing"
import { useListingView } from "@utils/hooks/useListingView"
import ResortSections from "@components/Resort/ResortSections"

const { Title } = Typography

type Props = {
    data: Listing
    statistic: any
    dealScoreData: any
    pointChartData: any
    jsonLd: object
}

export function ListingClientContent({ data: listing, statistic, dealScoreData, jsonLd, pointChartData }: Props) {
    const router = useRouter()
    const isFavorite = useFavorite(listing.id)
    const { topListings, meta } = useListingDealScore(listing, dealScoreData)
    useListingView(listing.id)
    useLayoutEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "instant"
        })
        let href = location.href
        setTimeout(() => {
            // force scroll to top because of bug on chrome iOS
            if (location.href === href) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "instant"
                })
            }
        }, 200)
    }, [])
    const onLike = useCallback(() => {
        updateListingLike({
            id: listing.id,
            like: !isFavorite
        })
    }, [listing, isFavorite])
    const { getLast } = useHistory()
    const mobileChart = useMediaQuery("(max-width: 768px)", true)
    const data = useMemo(() => (listing ? prepareData(statistic, listing, mobileChart) : {}), [statistic, listing, mobileChart])
    const onGoBack = (e) => {
        e.preventDefault()
        let lastUrl = ""
        try {
            lastUrl = new URL(location.origin + getLast())?.pathname
        } catch (e) {}

        if (["/listings", "/hottest-deals"].some((i) => i === lastUrl)) {
            router.back()
        } else {
            router.push("/listings")
        }
    }

    if (listing) {
        const group = findGroup(listing.points_on_contract)
        const resort = listing?.resort

        const likeBtn = (
            <button aria-label="Add to favorites" onClick={onLike} className={styles.likeBtn}>
                <span>Add to favorites </span>
                <Like className={cn(isFavorite && styles.heartIconActive)} />
            </button>
        )
        const title = (
            <Title className={styles.h1} level={1}>
                <span className={styles.id}>ID: {listing.id}</span>
                <span className={styles.pageTitle}>
                    {resort?.short_name} {listing?.resort?.id === 11 && Number(listing.deed_exp) === 2057 && " (2057)"}
                </span>
            </Title>
        )
        const topInfo = (
            <>
                <Link onClick={onGoBack} href="/listings" className={styles.linkBack}>
                    <ArrowDown />
                    back to listings
                </Link>
                {title}
                <div className={styles.statusWrap}>
                    <ListingStatus listing={listing} />
                </div>
            </>
        )
        const dues = resort?.resort_due?.data
        return (
            <>
                <JsonLd data={jsonLd} />
                <main className={styles.root}>
                    <div className={styles.row1}>
                        {topInfo}
                        {likeBtn}
                    </div>
                    <SlideAppear className={styles.content}>
                        <SoloResortChart
                            bottom={
                                <div className={styles.chartDescription}>
                                    Avg resale price of a {resort.short_name} contract with a point allotment between {group?.from}-{group?.to} points. Compare all resort averages
                                    <Link href="/blog/average-resale-prices">here.</Link>
                                </div>
                            }
                            withCurrent
                            withUpdates={true}
                            data={data}
                            subTitle={resort?.short_name}
                            classNames={styles.chart}
                            right={false}
                        />

                        <DealScore listing={listing} topListings={topListings} total={meta?.pagination?.total} />
                        {/*<PointsAvailability listing={listing} />*/}
                        <ListingPricing withBidSuggestion listing={listing} className={styles.pricing} />
                        <PointsSummary chartData={pointChartData} resort={resort} points={listing.points_on_contract} />
                        {dues && <HistoricalDues dues={dues} points_on_contract={listing.points_on_contract} />}
                    </SlideAppear>
                    {!!listing.resort?.description && (
                        <>
                            <div className={styles.resortGuid}>
                                Resort Guide
                                <div className={styles.line} />
                            </div>
                            <Suspense fallback={"Loading..."}>
                                <ResortSections resort={listing?.resort} level={3} />
                            </Suspense>
                        </>
                    )}
                </main>
            </>
        )
    }
    return (
        <div className={styles.loadingContainer}>
            <Spin tip="Loading..." size="large" />
        </div>
    )
}
